import React from 'react';
import Avatar from '../../../../components/shared/Avatar/Avatar';
import { FeedPost } from '../../../types/feed';
import { formatRelativeTime } from '../../../utils/formatRelativeTime';
import {
  getComments,
  MAX_COMMENT_LENGTH,
  createComment,
  deleteComment as deleteCommentApi,
  canDeleteComment,
  likePost as likePostApi,
  unlikePost as unlikePostApi,
  sharePost as sharePostApi,
  unsharePost as unsharePostApi,
  likeComment as likeCommentApi,
  unlikeComment as unlikeCommentApi,
  PostComment,
} from '../../../../services/posts/api';
import { getPublicProfile, PublicProfile } from '../../../../services/social/api';
import PhotoLightbox from '../PhotoLightbox/PhotoLightbox';
import ReportModal, { ReportTarget } from '../../social/ReportModal';
import './FeedPostCard.css';

interface FeedPostCardProps {
  post: FeedPost;
  currentUserId?: string;
  onLike?: (postId: string) => void;
  onComment?: (postId: string) => void;
  onShare?: (postId: string) => void;
  onEdit?: (postId: string) => void;
  onDelete?: (postId: string) => void;
}

// Commenter display names, resolved once and shared across every card.
const nameCache = new Map<string, string>();

const displayName = (profile: PublicProfile | null): string => {
  if (!profile) return 'Traveler';
  const full = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim();
  return full || profile.nickname || 'Traveler';
};

const resolveCommenterName = async (userId: string): Promise<string> => {
  const cached = nameCache.get(userId);
  if (cached) return cached;
  try {
    const name = displayName(await getPublicProfile(userId));
    nameCache.set(userId, name);
    return name;
  } catch {
    return 'Traveler';
  }
};

const PREVIEW_COMMENT_LIMIT = 3;

const FeedPostCard: React.FC<FeedPostCardProps> = ({
  post,
  currentUserId = '',
  onLike,
  onComment,
  onShare,
  onEdit,
  onDelete,
}) => {
  const [replyText, setReplyText] = React.useState('');
  const [menuOpen, setMenuOpen] = React.useState(false);
  // T2.12: what the report modal is aimed at (a post or a specific comment), or null.
  const [reportTarget, setReportTarget] = React.useState<ReportTarget | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const avatarSrc = post.author.avatarUrl || post.author.avatarEmoji;
  const galleryImages = post.imageUrls || (post.imageUrl ? [post.imageUrl] : []);
  // The viewer gets originals; the grid above keeps the thumbnails. Falls back
  // per-index so a post with only thumbnails still opens.
  const fullSizeImages = galleryImages.map(
    (thumb, index) => post.imageFullUrls?.[index] || thumb,
  );
  /**
   * The mosaic, Facebook-style and matching the mobile card: 1 photo fills the
   * block, 2 split it in half, 3 are a tall one plus two stacked, and 4 or more
   * are a 2x2 with the rest counted on the last tile. Two photos used to run
   * through the 3-up grid, which left the bottom-right cell empty.
   */
  const visibleImages = galleryImages.slice(0, 4);
  const hiddenImageCount = galleryImages.length - visibleImages.length;
  const galleryLayout = ['', '', 'pair', 'trio', 'quad'][Math.min(galleryImages.length, 4)];
  const canReply = replyText.trim().length > 0;
  const isOwner = Boolean(currentUserId) && post.author.id === currentUserId;
  // Owners get edit/delete; everyone else gets Report — so the menu shows for
  // both, just with different items (T2.12).
  const showPostMenu = isOwner ? Boolean(onEdit || onDelete) : Boolean(currentUserId);

  React.useEffect(() => {
    if (!menuOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Comments (T2.10d). Preview the latest few on load; expand for the full thread.
  const [comments, setComments] = React.useState<PostComment[]>([]);
  const [commentNames, setCommentNames] = React.useState<Record<string, string>>({});
  const [commentsExpanded, setCommentsExpanded] = React.useState(false);
  const [commentsLoaded, setCommentsLoaded] = React.useState(false);
  const [loadingComments, setLoadingComments] = React.useState(false);
  const [commentError, setCommentError] = React.useState<string | null>(null);
  /** Cursor for OLDER comments; absent once the thread's start is reached. */
  const [earlierBefore, setEarlierBefore] = React.useState<string | undefined>(undefined);
  const [loadingEarlier, setLoadingEarlier] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [commentCount, setCommentCount] = React.useState(post.comments);
  // Likes & shares (T2.10d) — self-contained like the comment thread: optimistic
  // toggle, then reconcile with the server's authoritative counts (rolled back on
  // error). The parent onLike/onShare still fire as notifications.
  const [liked, setLiked] = React.useState(Boolean(post.isLiked));
  const [likeCount, setLikeCount] = React.useState(post.likes);
  const [shareCount, setShareCount] = React.useState(post.shares ?? 0);
  const [sharedByMe, setSharedByMe] = React.useState(Boolean(post.sharedByMe));
  const [sharing, setSharing] = React.useState(false);
  const [repostAuthorName, setRepostAuthorName] = React.useState('Traveler');
  const [lightboxIndex, setLightboxIndex] = React.useState<number | null>(null);
  // Portrait photos, tracked per index as each one decodes. A vertical shot in a
  // `cover` box loses most of its subject to the crop, so it gets shown whole at
  // the block's full height instead.
  const [portraitIndexes, setPortraitIndexes] = React.useState<Set<number>>(new Set());

  const closeLightbox = React.useCallback(() => setLightboxIndex(null), []);

  const noteOrientation = React.useCallback(
    (index: number, image: HTMLImageElement) => {
      if (image.naturalHeight <= image.naturalWidth) return;
      setPortraitIndexes((previous) => {
        if (previous.has(index)) return previous;
        const next = new Set(previous);
        next.add(index);
        return next;
      });
    },
    [],
  );

  React.useEffect(() => {
    setCommentCount(post.comments);
  }, [post.comments]);

  const resolveNames = React.useCallback(async (rows: PostComment[]) => {
    const ids = Array.from(new Set(rows.map((c) => c.authorId)));
    const entries = await Promise.all(ids.map(async (id) => [id, await resolveCommenterName(id)] as const));
    setCommentNames((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
  }, []);

  const loadComments = React.useCallback(async () => {
    setLoadingComments(true);
    setCommentError(null);
    try {
      const page = await getComments(post.id, post.author.id);
      setComments(page.comments);
      setEarlierBefore(page.nextBefore);
      setCommentsLoaded(true);
      void resolveNames(page.comments);
    } catch {
      setCommentError('Could not load comments.');
    } finally {
      setLoadingComments(false);
    }
  }, [post.id, post.author.id, resolveNames]);

  /**
   * Fetch the page BEFORE the oldest one on screen and prepend it.
   *
   * The server returns a thread oldest-first and pages BACKWARDS, so older
   * comments belong at the top — appending would interleave them wrongly. The
   * page size is 50, so this only appears on genuinely long threads.
   */
  const loadEarlierComments = React.useCallback(async () => {
    if (!earlierBefore || loadingEarlier) return;
    setLoadingEarlier(true);
    try {
      const page = await getComments(post.id, post.author.id, earlierBefore);
      setComments((current) => {
        const seen = new Set(current.map((comment) => comment.commentId));
        return [...page.comments.filter((c) => !seen.has(c.commentId)), ...current];
      });
      setEarlierBefore(page.nextBefore);
      void resolveNames(page.comments);
      // Older comments are only worth fetching if they will be shown.
      setCommentsExpanded(true);
    } catch {
      setCommentError('Could not load earlier comments.');
    } finally {
      setLoadingEarlier(false);
    }
  }, [earlierBefore, loadingEarlier, post.id, post.author.id, resolveNames]);

  React.useEffect(() => {
    if (commentCount <= 0 || commentsLoaded || loadingComments) return;
    void loadComments();
  }, [commentCount, commentsLoaded, loadingComments, loadComments]);

  const visibleComments = commentsExpanded
    ? comments
    : comments.slice(-PREVIEW_COMMENT_LIMIT);
  const hasHiddenComments = comments.length > PREVIEW_COMMENT_LIMIT && !commentsExpanded;
  const showCommentsSection = loadingComments || (commentsLoaded && comments.length > 0);

  const handleCommentAction = () => {
    if (hasHiddenComments) {
      setCommentsExpanded(true);
    }
    onComment?.(post.id);
  };

  const handleReplySubmit = async () => {
    if (!canReply || submitting) return;
    setSubmitting(true);
    setCommentError(null);
    try {
      const comment = await createComment(post.id, post.author.id, replyText.trim());
      setComments((prev) => [...prev, comment]);
      setCommentCount((prev) => prev + 1);
      setCommentsLoaded(true);
      void resolveNames([comment]);
      setReplyText('');
    } catch {
      setCommentError('Could not post your comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Remove a comment (GAP-19) — optimistic, and rolled back on failure.
   *
   * Unlike the badge poll, this DOES restore on error: the thread is the thing
   * the user is looking at, so a comment that silently reappears on the next
   * load is worse than one that never left. No confirm dialog — the action is
   * small, and a comment can be retyped; the post-level delete keeps its modal
   * because that one is not recoverable.
   */
  const handleDeleteComment = async (commentId: string) => {
    const previous = comments;
    setComments((current) => current.filter((item) => item.commentId !== commentId));
    setCommentCount((current) => Math.max(0, current - 1));
    setCommentError(null);
    try {
      await deleteCommentApi(post.id, post.author.id, commentId);
    } catch {
      setComments(previous);
      setCommentCount(previous.length);
      setCommentError('Could not delete that comment. Please try again.');
    }
  };

  // Like a post — optimistic, reconciled to the server's count, rolled back on error.
  const handleLike = async () => {
    const next = !liked;
    setLiked(next);
    setLikeCount((count) => Math.max(0, count + (next ? 1 : -1)));
    onLike?.(post.id);
    try {
      const state = next
        ? await likePostApi(post.id, post.author.id)
        : await unlikePostApi(post.id, post.author.id);
      setLiked(state.likedByMe);
      setLikeCount(state.likes);
    } catch {
      setLiked(!next);
      setLikeCount((count) => Math.max(0, count + (next ? -1 : 1)));
    }
  };

  // Share (repost) / un-share — a repost IS a new post by the viewer, so on
  // success it will appear in their own feed on the next load.
  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    const next = !sharedByMe;
    setSharedByMe(next);
    setShareCount((count) => Math.max(0, count + (next ? 1 : -1)));
    onShare?.(post.id);
    try {
      if (next) await sharePostApi(post.id, post.author.id);
      else await unsharePostApi(post.id);
    } catch {
      setSharedByMe(!next);
      setShareCount((count) => Math.max(0, count + (next ? -1 : 1)));
    } finally {
      setSharing(false);
    }
  };

  const handleCommentLike = async (comment: PostComment) => {
    const next = !comment.likedByMe;
    const bump = (c: PostComment, on: boolean): PostComment =>
      c.commentId === comment.commentId
        ? { ...c, likedByMe: on, likes: Math.max(0, (c.likes ?? 0) + (on ? 1 : -1)) }
        : c;
    setComments((cur) => cur.map((c) => bump(c, next)));
    try {
      const state = next
        ? await likeCommentApi(post.id, comment.commentId, post.author.id)
        : await unlikeCommentApi(post.id, comment.commentId, post.author.id);
      setComments((cur) =>
        cur.map((c) => (c.commentId === comment.commentId ? { ...c, likedByMe: state.likedByMe, likes: state.likes } : c)));
    } catch {
      setComments((cur) => cur.map((c) => bump(c, !next)));
    }
  };

  // Resolve the ORIGINAL author's display name for a repost card (same cache the
  // comments use).
  React.useEffect(() => {
    if (!post.repost) return;
    void resolveCommenterName(post.repost.authorId).then(setRepostAuthorName);
  }, [post.repost]);

  return (
    <article className="v2-feed-post-card">
      <header className="v2-feed-post-card__header">
        <div className="v2-feed-post-card__author">
          <Avatar
            src={avatarSrc}
            alt={post.author.name}
            size="medium"
            className="v2-feed-post-card__avatar"
          />
          <div className="v2-feed-post-card__author-info">
            <span className="v2-feed-post-card__author-name">{post.author.name}</span>
            <span className="v2-feed-post-card__location">{post.location}</span>
          </div>
        </div>
        <time className="v2-feed-post-card__time" dateTime={post.createdAt}>
          {formatRelativeTime(post.createdAt)}
        </time>
        {showPostMenu && (
          <div className="v2-feed-post-card__menu" ref={menuRef}>
            <button
              type="button"
              className="v2-feed-post-card__menu-trigger"
              aria-label="Post options"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <circle cx="12" cy="5" r="1.75" />
                <circle cx="12" cy="12" r="1.75" />
                <circle cx="12" cy="19" r="1.75" />
              </svg>
            </button>
            {menuOpen && (
              <div className="v2-feed-post-card__menu-dropdown" role="menu" aria-label="Post options">
                {onEdit && (
                  <button
                    type="button"
                    role="menuitem"
                    className="v2-feed-post-card__menu-item"
                    onClick={() => {
                      setMenuOpen(false);
                      onEdit(post.id);
                    }}
                  >
                    Edit
                  </button>
                )}
                {isOwner && onDelete && (
                  <button
                    type="button"
                    role="menuitem"
                    className="v2-feed-post-card__menu-item v2-feed-post-card__menu-item--danger"
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete(post.id);
                    }}
                  >
                    Delete
                  </button>
                )}
                {/* T2.12: reporting is for OTHER people's posts, not your own. */}
                {!isOwner && (
                  <button
                    type="button"
                    role="menuitem"
                    className="v2-feed-post-card__menu-item"
                    onClick={() => {
                      setMenuOpen(false);
                      setReportTarget({
                        targetType: 'post',
                        targetId: post.id,
                        targetOwnerId: post.author.id,
                        label: 'this post',
                      });
                    }}
                  >
                    Report post
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {post.badge && (
          <button type="button" className="v2-feed-post-card__badge">
            {post.badge}
          </button>
        )}
      </header>

      {(galleryImages.length > 0 || post.imageEmoji) && (
        <div
          className={`v2-feed-post-card__media${
            galleryImages.length > 1
              ? ` v2-feed-post-card__media--gallery v2-feed-post-card__media--${galleryLayout}`
              : ''
          }`}
        >
          {galleryImages.length > 0 ? (
            visibleImages.map((imageUrl, index) => {
              const isLastTile = index === visibleImages.length - 1;
              return (
              <button
                type="button"
                key={`${imageUrl}-${index}`}
                className={`v2-feed-post-card__image-wrap v2-feed-post-card__image-wrap--${index + 1}${
                  portraitIndexes.has(index) ? ' v2-feed-post-card__image-wrap--portrait' : ''
                }`}
                onClick={() => setLightboxIndex(index)}
                aria-label={
                  isLastTile && hiddenImageCount > 0
                    ? `Open photos, ${hiddenImageCount} more`
                    : `Open photo ${index + 1} of ${galleryImages.length}`
                }
              >
                <img
                  src={imageUrl}
                  alt=""
                  className="v2-feed-post-card__image"
                  onLoad={(event) => noteOrientation(index, event.currentTarget)}
                />
                {isLastTile && hiddenImageCount > 0 && (
                  <span className="v2-feed-post-card__more">+{hiddenImageCount}</span>
                )}
                {isLastTile && galleryImages.length === 3 && !!post.savedCount && (
                  <span className="v2-feed-post-card__more">+{post.savedCount}</span>
                )}
              </button>
              );
            })
          ) : (
            <span className="v2-feed-post-card__image-placeholder">{post.imageEmoji}</span>
          )}
        </div>
      )}

      {post.caption && <p className="v2-feed-post-card__caption">{post.caption}</p>}

      {/* Repost (T2.10d): the shared original, re-gated server-side. Privacy
          outranks virality — if the original went closed or was deleted it comes
          back as this placeholder (also the GAP-21 shape). */}
      {(post.repost || post.repostUnavailable) && (
        <div className="v2-feed-post-card__repost">
          {post.repost ? (
            <>
              <span className="v2-feed-post-card__repost-author">{repostAuthorName}</span>
              {post.repost.caption && (
                <p className="v2-feed-post-card__repost-caption">{post.repost.caption}</p>
              )}
              {post.repost.imageUrls && post.repost.imageUrls.length > 0 && (
                <img className="v2-feed-post-card__repost-image" src={post.repost.imageUrls[0]} alt="" />
              )}
              <span className="v2-feed-post-card__repost-meta">
                {post.repost.likes} likes · {post.repost.comments} replies
              </span>
            </>
          ) : (
            <span className="v2-feed-post-card__repost-unavailable">
              This post is no longer available.
            </span>
          )}
        </div>
      )}

      <footer className="v2-feed-post-card__actions">
        <button
          type="button"
          className={`v2-feed-post-card__action ${liked ? 'v2-feed-post-card__action--active' : ''}`}
          onClick={handleLike}
          aria-pressed={liked}
          aria-label={`${liked ? 'Unlike' : 'Like'} post, ${likeCount} likes`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill={liked ? 'currentColor' : 'none'}
            />
          </svg>
          <span>{likeCount}</span>
        </button>
        <button
          type="button"
          className="v2-feed-post-card__action"
          onClick={handleCommentAction}
          aria-expanded={commentsExpanded}
          aria-label={`Comment, ${commentCount} comments`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{commentCount} replies</span>
        </button>
        <button
          type="button"
          className={`v2-feed-post-card__action ${sharedByMe ? 'v2-feed-post-card__action--active' : ''}`}
          onClick={handleShare}
          disabled={sharing}
          aria-pressed={sharedByMe}
          aria-label={`${sharedByMe ? 'Undo repost' : 'Repost'}${shareCount ? `, ${shareCount} shares` : ''}`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="16 6 12 2 8 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line x1="12" y1="2" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>{shareCount > 0 ? shareCount : 'Share'}</span>
        </button>
      </footer>

      {showCommentsSection && (
        <div className="v2-feed-post-card__comments">
          {loadingComments && <p className="v2-feed-post-card__comments-status">Loading comments…</p>}
          {visibleComments.map((comment) => (
            <div key={comment.commentId} className="v2-feed-post-card__comment">
              <span className="v2-feed-post-card__comment-author">
                {commentNames[comment.authorId] || 'Traveler'}
              </span>
              <span className="v2-feed-post-card__comment-text">{comment.text}</span>
              <time className="v2-feed-post-card__comment-time" dateTime={comment.createdAt}>
                {formatRelativeTime(comment.createdAt)}
              </time>
              {Boolean(currentUserId) && (
                <button
                  type="button"
                  className={`v2-feed-post-card__comment-like ${comment.likedByMe ? 'v2-feed-post-card__comment-like--active' : ''}`}
                  onClick={() => { void handleCommentLike(comment); }}
                  aria-pressed={Boolean(comment.likedByMe)}
                  aria-label={`${comment.likedByMe ? 'Unlike' : 'Like'} comment${comment.likes ? `, ${comment.likes} likes` : ''}`}
                >
                  ♥{comment.likes ? ` ${comment.likes}` : ''}
                </button>
              )}
              {canDeleteComment(comment, post.author.id, currentUserId) && (
                <button
                  type="button"
                  className="v2-feed-post-card__comment-delete"
                  onClick={() => { void handleDeleteComment(comment.commentId); }}
                  aria-label={
                    comment.authorId === currentUserId
                      ? 'Delete your comment'
                      : 'Remove this comment from your post'
                  }
                  title={
                    comment.authorId === currentUserId ? 'Delete' : 'Remove from your post'
                  }
                >
                  ×
                </button>
              )}
              {/* Report someone else's comment (T2.12). Not shown when you can
                  already delete it — take it down rather than report it. */}
              {Boolean(currentUserId) && !canDeleteComment(comment, post.author.id, currentUserId) && (
                <button
                  type="button"
                  className="v2-feed-post-card__comment-report"
                  onClick={() => setReportTarget({
                    targetType: 'comment',
                    targetId: comment.commentId,
                    targetOwnerId: comment.authorId,
                    label: 'this comment',
                  })}
                  aria-label="Report this comment"
                  title="Report"
                >
                  Report
                </button>
              )}
            </div>
          ))}
          {hasHiddenComments && (
            <button
              type="button"
              className="v2-feed-post-card__comments-more"
              onClick={() => setCommentsExpanded(true)}
            >
              View all {commentCount} replies
            </button>
          )}
          {/* Only on threads longer than one server page (50). Rendered ABOVE
              the list because it prepends older comments. */}
          {earlierBefore && (
            <button
              type="button"
              className="v2-feed-post-card__comments-more"
              onClick={() => { void loadEarlierComments(); }}
              disabled={loadingEarlier}
            >
              {loadingEarlier ? 'Loading…' : 'Show earlier comments'}
            </button>
          )}
        </div>
      )}

      {commentError && <p className="v2-feed-post-card__comment-error">{commentError}</p>}

      <div className="v2-feed-post-card__reply">
        <input
          type="text"
          className="v2-feed-post-card__reply-input"
          placeholder="Write a reply..."
          maxLength={MAX_COMMENT_LENGTH}
          value={replyText}
          onChange={(event) => setReplyText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleReplySubmit();
            }
          }}
          aria-label={`Reply to ${post.author.name}`}
        />
        <button
          type="button"
          className="v2-feed-post-card__reply-submit"
          onClick={handleReplySubmit}
          disabled={!canReply || submitting}
        >
          {submitting ? 'Posting…' : 'Reply'}
        </button>
      </div>

      <PhotoLightbox
        images={fullSizeImages}
        index={lightboxIndex}
        onClose={closeLightbox}
        onIndexChange={setLightboxIndex}
        label={`Photo from ${post.author.name}`}
      />

      {reportTarget && (
        <ReportModal target={reportTarget} onClose={() => setReportTarget(null)} />
      )}
    </article>
  );
};

export default FeedPostCard;
